import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useZones } from '../hooks/useWmsLocations';
import { useInventory, useAdjustInventory, useUpdateInventoryItem, useCreateManualInventoryItem, useDeleteInventoryItem, wmsInventoryKeys } from '../hooks/useWmsInventory';
import { useDebounce } from '../hooks/useDebounce';
import { getInventory } from '../api';
import { InventoryFilters } from '../components/inventory/InventoryFilters';
import { InventoryTable } from '../components/inventory/InventoryTable';
import { InventoryEditModal, type InventoryEditSubmitData } from '../components/inventory/InventoryEditModal';
import { AddInventoryItemModal } from '../components/inventory/AddInventoryItemModal';
import { InventoryDeleteDialog } from '../components/inventory/InventoryDeleteDialog';
import type { InventoryItem, ItemStatus, CreateManualInventoryInput, ZonesResponse, InventoryResponse } from '../types';

const PAGE_SIZE = 50;
const COLUMN_FILTERS_KEY = 'atek-wms-inventory-column-filters';
const SUPPLIER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SUPPLIER_ACCOUNT_NUMBER_PATTERN = /^A-\d{4}$/;

function normalizeSupplierFilterValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (SUPPLIER_ID_PATTERN.test(value) || SUPPLIER_ACCOUNT_NUMBER_PATTERN.test(value)) {
    return value;
  }
  return undefined;
}

function sanitizeColumnFilters(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};

  const filters = Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(([, value]) => typeof value === 'string'),
  ) as Record<string, string>;

  if (filters.supplier && !normalizeSupplierFilterValue(filters.supplier)) {
    delete filters.supplier;
  }

  return filters;
}

export default function InventoryListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Server-side filter state
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [zoneId, setZoneId] = useState('all');
  const [status, setStatus] = useState('all');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Column filters (client-side refinement, persisted to localStorage)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try { return sanitizeColumnFilters(JSON.parse(localStorage.getItem(COLUMN_FILTERS_KEY) || '{}')); }
    catch { return {}; }
  });

  const sanitizedColumnFilters = useMemo(() => {
    return sanitizeColumnFilters(columnFilters);
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_KEY, JSON.stringify(sanitizedColumnFilters));
  }, [sanitizedColumnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const selectedSupplierId = normalizeSupplierFilterValue(sanitizedColumnFilters.supplier);

  // Modal state
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<InventoryItem | null>(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);

  // Promote text column filters to server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(sanitizedColumnFilters)
      .filter(([key, value]) => {
        if (!value) return false;
        if (key === 'supplier') return false;
        if (value.includes('>>')) return false; // number-range
        if (value.startsWith('[')) return false; // multi-select
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [sanitizedColumnFilters]);

  const debouncedColumnSearch = useDebounce(columnSearchText, 300);

  const combinedSearch = [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ') || undefined;

  // Reset to first page when filters change
  useEffect(() => { setPage(0); }, [debouncedSearch]);
  useEffect(() => { if (debouncedColumnSearch) setPage(0); }, [debouncedColumnSearch]);

  // Queries
  const zonesQuery = useZones();
  const inventoryQuery = useInventory({
    zone_id: zoneId === 'all' ? undefined : zoneId,
    item_status: status === 'all' ? undefined : (status as ItemStatus),
    preferred_supplier_id: selectedSupplierId,
    low_stock: lowStockOnly || undefined,
    search: combinedSearch,
    page_number: page,
    page_length: PAGE_SIZE,
    sort_key: sortKey,
    sort_direction: sortKey ? sortDirection : undefined,
  });

  // Mutations
  const adjustMutation = useAdjustInventory();
  const updateItemMutation = useUpdateInventoryItem();
  const addItemMutation = useCreateManualInventoryItem();
  const deleteMutation = useDeleteInventoryItem();

  const zonesData = zonesQuery.data as ZonesResponse | undefined;
  const inventoryData = inventoryQuery.data as InventoryResponse | undefined;

  const zones = zonesData?.zones || [];
  const items = inventoryData?.items || [];
  const total = inventoryData?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleEdit = async (data: InventoryEditSubmitData) => {
    if (!editingItem) return;

    let currentItem = editingItem;

    if (data.update) {
      currentItem = await updateItemMutation.mutateAsync({
        id: currentItem.id,
        data: data.update,
      });
    }

    if (data.adjustment) {
      await adjustMutation.mutateAsync({
        ...data.adjustment,
        inventory_item_id: currentItem.id,
      });
    }

    setEditingItem(null);
  };

  const handleAddItem = async (data: CreateManualInventoryInput) => {
    await addItemMutation.mutateAsync(data);
    setAddItemOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (deletingItem) {
      await deleteMutation.mutateAsync(deletingItem.id);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.all });
  };

  const handleExportAll = async () => {
    try {
      setIsExportingAll(true);
      const allItems: InventoryItem[] = [];
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getInventory({
          zone_id: zoneId === 'all' ? undefined : zoneId,
          item_status: status === 'all' ? undefined : (status as ItemStatus),
          preferred_supplier_id: selectedSupplierId,
          low_stock: lowStockOnly || undefined,
          search: combinedSearch,
          page_number: pageNum,
          page_length: PAGE_SIZE,
          sort_key: sortKey,
          sort_direction: sortKey ? sortDirection : undefined,
        });
        allItems.push(...(batch.items || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allItems.length < totalCount);

      // Trigger CSV download using same logic as table
      const header = ['SKU', 'Name', 'Location', 'Supplier', 'On Hand', 'Unit', 'Reserved', 'Available', 'Status'].join(',');
      const rows = allItems.map((item) => [
        item.sku_code,
        item.sku_name || item.sku_details?.name?.en || '',
        item.full_location_code || `${item.zone_code || ''}-${item.bin_code || ''}`,
        item.preferred_supplier_name || '',
        item.quantity_on_hand,
        item.unit_of_measure || '',
        item.quantity_reserved || 0,
        item.quantity_available,
        item.item_status,
      ].map((v) => {
        const str = String(v ?? '');
        if (/^[=+\-@\t\r]/.test(str)) return `"'${str}"`;
        if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
        return str;
      }).join(','));

      const csvContent = [header, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wms-inventory-all_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${allItems.length} rows`);
    } catch {
      toast.error('Failed to export all data');
    } finally {
      setIsExportingAll(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Inventory <small>View and manage stock levels</small>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddItemOpen(true)}>Add Item</Button>
          <Button onClick={() => navigate('/procurement')}>Receive Stock</Button>
        </div>
      </div>

      {/* Server-side Filters */}
      <div className="flex items-center justify-between gap-4">
        <InventoryFilters
          search={search}
          onSearchChange={setSearch}
          zoneId={zoneId}
          onZoneChange={(v) => { setZoneId(v); setPage(0); }}
          status={status}
          onStatusChange={(v) => { setStatus(v); setPage(0); }}
          lowStockOnly={lowStockOnly}
          onLowStockChange={(v) => { setLowStockOnly(v); setPage(0); }}
          zones={zones}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} item{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <InventoryTable
        items={items}
        total={total}
        isLoading={inventoryQuery.isLoading}
        columnFilters={sanitizedColumnFilters}
        onColumnFilterChange={setColumnFilter}
        onClearColumnFilters={() => { setColumnFilters({}); setPage(0); }}
        selectedSupplierId={selectedSupplierId}
        onSelectedSupplierChange={(supplierId) => {
          setColumnFilter('supplier', supplierId === 'all' ? '' : supplierId);
          setPage(0);
        }}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onEdit={(item) => setEditingItem(item)}
        onDelete={(item) => setDeletingItem(item)}
        onRefresh={handleRefresh}
        isRefreshing={inventoryQuery.isFetching}
        onExportAll={handleExportAll}
        isExportingAll={isExportingAll}
      />

      {/* Edit Modal */}
      <InventoryEditModal
        open={!!editingItem}
        onOpenChange={(open) => {
          if (!open) setEditingItem(null);
        }}
        item={editingItem}
        onSubmit={handleEdit}
        isLoading={updateItemMutation.isPending || adjustMutation.isPending}
      />

      {/* Delete Dialog */}
      <InventoryDeleteDialog
        open={!!deletingItem}
        onOpenChange={(open) => {
          if (!open) setDeletingItem(null);
        }}
        item={deletingItem}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
      />

      {/* Add Item Modal */}
      <AddInventoryItemModal
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        onSubmit={handleAddItem}
        isLoading={addItemMutation.isPending}
      />
    </div>
  );
}
